import { Radio, Select } from '@mantine/core'
import { isPressureValueType } from 'App/Map/CurrentValuesMap/_utils/by-domain/_utils/isPressureValueType'
import type { TValuesCurrentPoints, TValuesCurrentProperties } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { colors } from 'settings/colors'
import { HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD } from 'settings/hubConnectorEnvironment'
import { SharedSettings } from 'settings/SharedSettings'
import { mapControlsStore } from 'stores/mapControlsStore/mapControlsStore'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { mapSelectedCardStore } from 'stores/mapSelectedCardStore/mapSelectedCardStore'
import type { TAppliedIrrigationActivityDisplayOption } from 'stores/smallStore/smallStore'
import { smallStore } from 'stores/smallStore/smallStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { mantineSelectSearchFilterProp } from 'utils/mantineSelectSearchFilterProp'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { useSelectClickToggle } from 'utils/useSelectClickToggle'
import { useIrrigationSelectData } from '../_utils/useIrrigationSelectData'
import { EmitterSelect } from './EmitterSelect'

const updateMapVisuals = (value: string | null) => {
  if (value === null) return

  const newValueType = value as TValuesCurrentPoints

  mapControlsStore.setState((s) => ({
    ...s,
    mapVisualValueGroup: {
      ...s.mapVisualValueGroup,
      [MAP_VISUAL.POINT]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.POINT],
        irrigation_activity_pressure: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.POINT].irrigation_activity_pressure ?? {
            mItem: 'M_InBlockPoint',
          }),
          valueType: newValueType,
        },
      },
      [MAP_VISUAL.PROPERTY]: {
        ...s.mapVisualValueGroup[MAP_VISUAL.PROPERTY],
        irrigation_activity_pressure: {
          ...(s.mapVisualValueGroup[MAP_VISUAL.PROPERTY].irrigation_activity_pressure ?? {
            mItem: 'M_Property',
          }),
          valueType: newValueType.replace('Sum_', 'SumMinMedianMax_') as TValuesCurrentProperties,
        },
      },
    },
  }))
}

const radioStyles = {
  label: {
    color: HUB_CONNECTOR_ENVIRONMENT_IS_SWAGWORLD ? colors.midnight : colors.white,
    cursor: 'pointer',
  },
  radio: { cursor: 'pointer' },
}

const VOLUME: TAppliedIrrigationActivityDisplayOption = 'VOLUME'
const DURATION: TAppliedIrrigationActivityDisplayOption = 'DURATION'

export const IrrigationActivitySecondaryDropdown = () => {
  const isWideScreen = useIsWideScreen()
  const selectClickToggle = useSelectClickToggle()
  const data = useIrrigationSelectData({ includePressure: true })
  const appliedIrrigationActivityDisplay = smallStore.useSelector((s) => s.appliedIrrigationActivityDisplay)

  const selectedValueType = mapControlsStore.useSelector(
    (s) => s.mapVisualValueGroup?.[MAP_VISUAL.POINT]?.irrigation_activity_pressure?.valueType,
  )

  const precipitationUnit = userDetailsStore.useSelector((s) => s?.rain)

  const showAppliedIrrigationOptions =
    selectedValueType &&
    !selectedValueType?.endsWith('_status') &&
    !selectedValueType?.endsWith('_interval') &&
    !isPressureValueType(selectedValueType)

  return (
    <div css={{ display: 'flex', flexDirection: 'column' }}>
      <div
        onClick={() => mapSelectedCardStore.actions.hide()}
        css={{ display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        <Select
          {...selectClickToggle}
          data={data}
          filter={mantineSelectSearchFilterProp}
          onChange={updateMapVisuals}
          styles={SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER}
          searchable={isWideScreen}
          value={selectedValueType}
          comboboxProps={{ withinPortal: true }}
        />
        <div css={{ maxWidth: 300 }}>
          <EmitterSelect />
        </div>
      </div>
      {showAppliedIrrigationOptions && (
        <Radio.Group
          css={{ marginTop: 12 }}
          onChange={(newVal) =>
            smallStore.setState((s) => ({
              ...s,
              appliedIrrigationActivityDisplay: newVal as TAppliedIrrigationActivityDisplayOption,
            }))
          }
          value={appliedIrrigationActivityDisplay}
        >
          <div css={{ display: 'flex', flexWrap: 'wrap', gap: '0px 12px' }}>
            <Radio
              label={translate.phrases.templates('{{label}} ({{unitSymbol}})', {
                label: translate.phrases.banyanApp('Duration'),
                unitSymbol: translate.phrases.banyanApp('Hours'),
              })}
              styles={radioStyles}
              value={DURATION}
            />
            <Radio
              label={translate.phrases.templates('{{label}} ({{unitSymbol}})', {
                label: translate.phrases.banyanApp('Volume'),
                unitSymbol: unitConverter.precipitation().suffix(),
              })}
              styles={radioStyles}
              value={VOLUME}
              key={precipitationUnit} // lets use this useSelector value to force a rerender of this component when the unit changes
            />
          </div>
        </Radio.Group>
      )}
    </div>
  )
}
